/**
 * 生成 API の URL パターン表。
 *
 * axios のリクエストから「生成が始まった」を画面コードを触らずに検出するための
 * 対応表。表に無い URL は記録しない（誤検知より取りこぼしを選ぶ）。
 * URL は先頭スラッシュと `api/` を除いた相対パス。
 *
 * 完了/失敗はサーバー側（GenerationOutcomeRecorder）が記録するので、
 * ジョブ状態のポーリング URL はここで持たない。
 *
 * 出典（2026-09-07 時点の services/api/*Service.ts）:
 *   images/generate | generate-async | generate-multiple | generate-with-urls
 *   images/edit | edit-async
 *   videos/generate-async | generate-batch
 *   manga/projects/{id}/pages/{n}/generate
 *   movies/{id}/concat, movies/{id}/scenes/{s}/clip
 *   song-ads/{id}/(generate-song|compose|scenes/{s}/clip)
 *   ugc-ads/{id}/(compose|scenes/{s}/clip)
 *   music/projects
 *   lp/projects/{id}/generate-design
 */

export interface GenerationSignal {
  /** 固定の type、または現在の画面パスから決める関数（画像生成はページで用途が分かれる） */
  type: string | ((pathname: string) => string);
  start?: RegExp[];
}

/** 画像生成 API は複数画面で共用されるので、画面パスから type を決める */
export const imageTypeFromPage = (pathname: string): string => {
  if (pathname.startsWith('/ads/carousel')) return 'carousel';
  if (pathname.startsWith('/image-generation')) return 'image';
  if (pathname.startsWith('/onboarding') || pathname.startsWith('/signup') || pathname.startsWith('/first-login')) {
    return 'onboarding';
  }
  return 'banner';
};

export const GENERATION_SIGNALS: GenerationSignal[] = [
  { type: imageTypeFromPage, start: [/^images\/generate(-async|-multiple|-with-urls)?$/] },
  { type: 'image_edit', start: [/^images\/edit(-async)?$/] },
  { type: 'video', start: [/^videos\/generate-(async|batch)$/] },
  { type: 'manga', start: [/^manga\/projects\/[^/]+\/pages\/[^/]+\/generate$/] },
  { type: 'movie', start: [/^movies\/[^/]+\/(concat|scenes\/[^/]+\/clip)$/] },
  { type: 'song', start: [/^song-ads\/[^/]+\/(generate-song|compose|scenes\/[^/]+\/clip)$/] },
  { type: 'ugc', start: [/^ugc-ads\/[^/]+\/(compose|scenes\/[^/]+\/clip)$/] },
  { type: 'music', start: [/^music\/projects$/] },
  { type: 'lp', start: [/^lp\/projects\/[^/]+\/generate-design$/] },
];

/** 絶対 URL / 先頭スラッシュ / api プレフィックス / クエリを除いた相対パスにする */
export const toRelativeApiPath = (url: string): string => {
  const noQuery = url.split('?')[0].split('#')[0];
  const noOrigin = noQuery.replace(/^https?:\/\/[^/]+/, '');
  return noOrigin.replace(/^\/+/, '').replace(/^api\//, '');
};

const resolveType = (signal: GenerationSignal, pathname: string): string =>
  typeof signal.type === 'function' ? signal.type(pathname) : signal.type;

/** 生成開始の POST なら type を返す */
export const matchGenerationStart = (method: string, url: string, pathname: string): string | null => {
  if (method.toUpperCase() !== 'POST') return null;
  const path = toRelativeApiPath(url);
  for (const signal of GENERATION_SIGNALS) {
    if (signal.start?.some((re) => re.test(path))) {
      return resolveType(signal, pathname);
    }
  }
  return null;
};
